import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Card, CardContent, Container, Stack, TextField, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { PRICING, formatPrice } from "../config/pricing";

export default function MediaKitPremium() {
  const navigate = useNavigate();
  const paymentLink = PRICING?.paymentLink || "";
  const price = PRICING?.price;
  const oldPrice = PRICING?.oldPrice;
  const currency = PRICING?.currency || "EUR";
  const baseUrl = import.meta.env.BASE_URL;
  // Aktions-Code ist fix vorbelegt: jeder zahlt automatisch den Aktionspreis.
  // Wer einen anderen Gutschein hat, kann ihn im Feld überschreiben.
  const [promoCode, setPromoCode] = useState(PRICING?.promoCode || "");
  const [showPromoField, setShowPromoField] = useState(false);

  const buildPaymentUrl = (base, code) => {
    if (!base) return "";
    if (!code) return base;
    try {
      const url = new URL(base);
      url.searchParams.set("prefilled_promo_code", code);
      return url.toString();
    } catch (err) {
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}prefilled_promo_code=${encodeURIComponent(code)}`;
    }
  };

  const normalizedPromoCode = promoCode.trim();
  const paymentUrl = useMemo(
    () => buildPaymentUrl(paymentLink, normalizedPromoCode),
    [paymentLink, normalizedPromoCode]
  );
  const heroCtaTag = paymentUrl ? "a" : "div";

  const heroBenefits = [
    "Unbegrenzt viele Media-Kits erstellen.",
    "Design-Editor für Layout, Farben und Inhalte.",
    "Videokurs + Profi-Expertise Schritt für Schritt.",
    "PDF Export für Print & Digital inkl. QR-Code.",
  ];

  const stats = [
    { strong: "2 Jahre", label: "Zugang" },
    { strong: "8 Lektionen", label: "Videokurs" },
    { strong: "PDF", label: "Export" },
  ];

  const valueCards = [
    {
      title: "Schnell zum fertigen Kit",
      text: "Du brauchst nur die wichtigsten Infos. Der Generator baut dir das fertige Layout in Minuten.",
    },
    {
      title: "Optik, die bucht",
      text: "Layouts, Typo und Farben sind so gebaut, dass Veranstalter sofort Vertrauen haben.",
    },
    {
      title: "Prozess statt Chaos",
      text: "Video-Guide, Checklisten und Beispiele. Du weißt immer, was als nächstes kommt.",
    },
  ];

  const included = [
    "Unbegrenzte Media-Kit Projekte",
    "Design-Editor mit Vorlagen",
    "Videokurs mit 8 Lektionen",
    "QR-Code & Link-Module",
    "PDF Export (Print & Digital)",
  ];

  return (
    <Box component="main">
      <Box
        component="section"
        aria-label="Mediakit Premium - Hero"
        className="sectionBg sectionA"
        sx={(theme) => ({
          position: "relative",
          overflow: "hidden",
          py: { xs: 7, md: 10 },
          pt: { xs: 9, md: 12 },
          background:
            theme.palette.mode === "dark"
              ? "radial-gradient(1100px 520px at 12% 0%, rgba(163,216,30,0.22), transparent 56%), radial-gradient(900px 500px at 90% 18%, rgba(23,23,15,0.18), transparent 56%)"
              : "radial-gradient(1100px 520px at 12% 0%, rgba(163,216,30,0.14), transparent 56%), radial-gradient(900px 500px at 90% 18%, rgba(23,23,15,0.10), transparent 56%)",
        })}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
              gap: { xs: 4, md: 8 },
              alignItems: "center",
            }}
          >
            <Stack spacing={3.5} sx={{ order: { xs: 1, md: 0 } }}>
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 2 }}>
                Media-Kit Premium
              </Typography>
              <Typography
                sx={{
                  fontWeight: 900,
                  letterSpacing: "-0.05em",
                  lineHeight: 1.02,
                  fontSize: { xs: "2.4rem", sm: "3rem", md: "3.6rem" },
                }}
              >
                Ein Media-Kit, das Veranstalter sofort überzeugt.
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: { xs: "1.08rem", md: "1.15rem" } }}>
                Klare Struktur, starker Look und ein geführter Prozess. Baue in Minuten ein Kit, das Bookings bringt.
              </Typography>


              <Stack spacing={1.4}>
                {heroBenefits.map((item) => (
                  <Stack key={item} direction="row" spacing={1.2} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        bgcolor: "rgba(16,185,129,0.12)",
                        display: "grid",
                        placeItems: "center",
                        mt: "2px",
                      }}
                    >
                      <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "success.main" }} />
                    </Box>
                    <Typography
                      color="text.secondary"
                      sx={{ fontWeight: 700, fontSize: { xs: "1.02rem", md: "1.08rem" }, lineHeight: 1.4 }}
                    >
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.2,
                  maxWidth: 460,
                }}
              >
                {stats.map((x) => (
                  <Stack
                    key={x.strong}
                    direction="row"
                    spacing={0.6}
                    alignItems="center"
                    sx={{
                      px: 1.6,
                      py: 0.8,
                      bgcolor: "action.hover",
                      borderRadius: 999,
                      minHeight: 36,
                      justifyContent: "center",
                      flex: "0 1 auto",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                      {x.strong}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {x.label}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Stack>

            <Stack
              spacing={2}
              alignItems="center"
              sx={{ height: "100%", justifyContent: "center", order: { xs: -1, md: 0 } }}
            >
              <Box
                component={heroCtaTag}
                href={paymentUrl || undefined}
                target={paymentUrl ? "_blank" : undefined}
                rel={paymentUrl ? "noopener noreferrer" : undefined}
                aria-label="Premium kaufen"
                sx={{
                  display: "block",
                  textDecoration: "none",
                  cursor: paymentUrl ? "pointer" : "default",
                  transition: "transform 180ms ease",
                  "&:hover": paymentUrl ? { transform: "translateY(-6px)" } : undefined,
                }}
              >
                <Box
                  component="img"
                  src={baseUrl + "mockup-mediakit.png"}
                  alt="Mediakit Vorschau"
                  loading="eager"
                  sx={{
                    width: { xs: "86%", sm: "72%", md: "90%" },
                    maxWidth: { xs: 300, sm: 360, md: 520 },
                    height: "auto",
                    display: "block",
                    objectFit: "cover",
                    filter: "drop-shadow(0 28px 60px rgba(15,23,42,0.25))",
                    mx: "auto",
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Sicher bezahlen. Sofortiger Zugang.
              </Typography>
            </Stack>
          </Box>
        </Container>
      </Box>

{/*}
      <Box component="section" className="sectionBg sectionB" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Stack spacing={3}>
            <Stack spacing={1} textAlign={{ xs: "left", md: "center" }} alignItems={{ md: "center" }}>
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.6 }}>
                Warum Premium?
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
                Drei Gründe, warum es funktioniert.
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2.5,
              }}
            >
              {valueCards.map((card, index) => (
                <Card
                  key={card.title}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(148,163,184,0.35)",
                    bgcolor: "rgba(255,255,255,0.75)",
                    boxShadow: "0 18px 42px rgba(15,23,42,0.10)",
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 3.6 } }}>
                    <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: 2 }}>
                      0{index + 1}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6, mb: 1 }}>
                      {card.title}
                    </Typography>
                    <Typography color="text.secondary">{card.text}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Stack>
        </Container>
      </Box
      
      */}
      <Box component="section" className="sectionBg sectionA" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid rgba(148,163,184,0.35)",
              background: "linear-gradient(135deg, rgba(163,216,30,0.10), rgba(23,23,15,0.08))",
              boxShadow: "0 24px 80px rgba(15,23,42,0.12)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 4, md: 6 } }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
                  gap: { xs: 3, md: 6 },
                  alignItems: "center",
                }}
              >
                <Stack spacing={2} alignItems={{ xs: "center", md: "flex-start" }} textAlign={{ xs: "center", md: "left" }}>
                  <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                    Premium Zugang
                  </Typography>
                  <Stack spacing={0.6} alignItems={{ xs: "center", md: "flex-start" }}>
                    {oldPrice ? (
                      <Typography sx={{ color: "text.secondary", textDecoration: "line-through" }}>
                        {formatPrice(oldPrice, currency)}
                      </Typography>
                    ) : null}
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 900,
                        background: "linear-gradient(135deg, #0f172a 0%, #2563eb 70%)",
                        WebkitBackgroundClip: "text",
                        color: "transparent",
                      }}
                    >
                      {formatPrice(price, currency)}
                    </Typography>
                    <Typography color="text.secondary">Einmalig für 2 Jahre Zugang.</Typography>
                  </Stack>



                  <Stack spacing={1.2} sx={{ width: "100%", maxWidth: 360 }}>
                    {!showPromoField ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowPromoField(true)}
                        sx={{ borderRadius: 999, fontWeight: 700, textTransform: "none", alignSelf: { xs: "center", md: "flex-start" } }}
                      >
                        Gutscheincode einlösen
                      </Button>
                    ) : (
                      <TextField
                        label="Gutscheincode"
                        placeholder="Code eingeben"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        fullWidth
                        size="small"
                      />
                    )}
                    <Button
                      variant="contained"
                      size="large"
                      component="a"
                      href={paymentUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!paymentUrl}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 800,
                        textTransform: "none",
                        px: 3.6,
                        color: "#14150c",
                        background: "linear-gradient(135deg, #c3f04c 0%, #a3d81e 100%)",
                        boxShadow: "0 14px 32px rgba(23,23,15,0.35)",
                      }}
                    >
                      Jetzt kaufen
                    </Button>
                  </Stack>
                </Stack>

                <Stack
                  spacing={2}
                  alignItems={{ xs: "center", md: "flex-end" }}
                  sx={{ height: "100%", justifyContent: "center" }}
                >
                  <Box
                    sx={{
                      width: { xs: 170, md: 210 },
                      height: { xs: 170, md: 210 },
                      borderRadius: "50%",
                      background:
                        "repeating-conic-gradient(from 0deg, rgba(14,116,144,0.9) 0deg 10deg, rgba(6,182,212,0.9) 10deg 20deg)",
                      display: "grid",
                      placeItems: "center",
                      boxShadow: "0 20px 46px rgba(6,182,212,0.25)",
                      transform: "rotate(-4deg)",
                    }}
                  >
                    <Box
                      sx={{
                        width: "78%",
                        height: "78%",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #ffffff, #ecfeff)",
                        border: "2px solid rgba(6,182,212,0.45)",
                        display: "grid",
                        placeItems: "center",
                        textAlign: "center",
                        fontWeight: 900,
                        fontSize: { xs: "0.9rem", md: "1.02rem" },
                        lineHeight: 1.1,
                        color: "#0f172a",
                        letterSpacing: "0.02em",
                        px: 0.6,
                        maxWidth: "100%",
                        whiteSpace: "normal",
                      }}
                    >
                      30 Tage
                      <br />
                      Zufriedenheits-
                      <br />
                      garantie
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Sicher bezahlen. Sofortiger Zugang.
                  </Typography>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
}
